/**
 * Rental contract (Mietvertrag) domain API functions (Phase 3a.4).
 *
 * list / create-draft / preview. Preview is render-only (backend never mutates
 * on GET). All authenticated via getApiHeaders(); errors surfaced through
 * parseAdminErrorResponse, mirroring the other adminData domain modules.
 */
import { API_BASE_URL, getApiHeaders, parseAdminErrorResponse } from "./core";

/**
 * List rental contracts for a tenancy.
 * GET /api/admin/tenancies/{tenancyId}/rental-contracts -> RentalContractListItem[]
 */
export async function listRentalContracts(tenancyId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/rental-contracts`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Mietverträge konnten nicht geladen werden.");
  }
  return res.json();
}

/**
 * Create a draft rental contract for a tenancy (existing Sprint-2 endpoint).
 * POST /api/admin/tenancies/{tenancyId}/rental-contracts -> RentalContractDetail
 */
export async function createRentalContract(tenancyId, body = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/rental-contracts`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(body || {}),
    }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Vertragsentwurf konnte nicht erstellt werden.");
  }
  return res.json();
}

/**
 * Cancel a rental contract (soft-cancel; keeps artifacts). Phase 3a.5d.
 * POST /api/admin/rental-contracts/{contractId}/cancel
 *   body: { cancellation_reason } -> updated RentalContractDetail
 * signed/cancelled/superseded return 409 -> thrown as Error with the message.
 */
export async function cancelRentalContract(contractId, body = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/rental-contracts/${encodeURIComponent(contractId)}/cancel`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(body || {}),
    }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Vertrag konnte nicht storniert werden.");
  }
  return res.json();
}

/**
 * Render-only HTML preview of a rental contract.
 * GET /api/admin/rental-contracts/{contractId}/preview
 *   -> { html, warnings, blocked }
 * Unsupported cases return 422 -> thrown as Error with the backend message.
 */
export async function previewRentalContract(contractId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/rental-contracts/${encodeURIComponent(contractId)}/preview`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Vorschau konnte nicht geladen werden.");
  }
  return res.json();
}

/**
 * Generate (or, with force, re-generate) the rental-contract PDF. Phase 3a.7.
 * POST /api/admin/rental-contracts/{contractId}/generate-pdf[?force=true]
 *   -> RentalContractDetail
 * force is passed ONLY as the query param ?force=true (no JSON body).
 * Not-ready (422 + issues) / immutable status (409) surface as thrown Errors.
 */
export async function generateRentalContractPdf(contractId, { force = false } = {}) {
  const qs = force ? "?force=true" : "";
  const res = await fetch(
    `${API_BASE_URL}/api/admin/rental-contracts/${encodeURIComponent(contractId)}/generate-pdf${qs}`,
    {
      method: "POST",
      headers: getApiHeaders(),
    }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "PDF konnte nicht generiert werden.");
  }
  return res.json();
}

/**
 * Get a presigned URL for the stored generated PDF. Phase 3a.7.
 * GET /api/admin/rental-contracts/{contractId}/pdf/url -> { url }
 * 404 if no PDF exists yet -> thrown as Error. Uses the generated
 * pdf_object_key (NOT signed_pdf_object_key).
 */
export async function getRentalContractPdfUrl(contractId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/rental-contracts/${encodeURIComponent(contractId)}/pdf/url`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "PDF-Link konnte nicht geladen werden.");
  }
  return res.json();
}
